/**
 * Phase 7 — Admin observability tabs.
 * Used inside AdminConsole (Invoices + Payouts tabs).
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmt, money } from "./_shared";


export function AdminInvoicesTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stateFilter, setStateFilter] = useState("");
  const load = async () => {
    setLoading(true);
    try {
      const qs = stateFilter ? `?state=${stateFilter}` : "";
      const r = await api(`/admin/invoices${qs}`);
      setRows(r.data || []);
    } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [stateFilter]);
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <label className="text-sm">
          State:
          <select className="avcp-input ml-2" value={stateFilter}
                  onChange={(e) => setStateFilter(e.target.value)}>
            <option value="">All</option>
            {["draft", "issued", "paid", "settled", "voided"].map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </label>
      </div>
      <table className="w-full text-sm" data-testid="admin-invoices-table">
        <thead className="text-xs text-[var(--avcp-ink-soft)]">
          <tr><th className="text-left">Created</th><th>Booking</th><th>State</th>
              <th>Mode</th><th>Issuer</th><th>Review?</th>
              <th className="text-right">Total</th></tr>
        </thead>
        <tbody>
          {loading && <tr><td colSpan={7} className="py-3">Loading…</td></tr>}
          {!loading && rows.length === 0 && (
            <tr><td colSpan={7} className="py-3 text-[var(--avcp-ink-soft)]">No invoices.</td></tr>
          )}
          {rows.map((r) => (
            <tr key={r.id} data-testid="admin-invoices-row"
                className="border-t border-[var(--avcp-line)]">
              <td className="py-2">{fmt(r.created_at)}</td>
              <td className="font-mono text-xs">#{r.booking_id}</td>
              <td><span className="avcp-pill">{r.state}</span></td>
              <td className="text-xs">{r.billing_mode}</td>
              <td className="text-xs">{r.issuer_kind}</td>
              <td>{r.requires_review ? <span className="text-amber-700">⚠️ review</span> : "—"}</td>
              <td className="text-right">{money(r.total_amount, r.currency)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


export function AdminPayoutsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stateFilter, setStateFilter] = useState("");
  const load = async () => {
    setLoading(true);
    try {
      const qs = stateFilter ? `?state=${stateFilter}` : "";
      const r = await api(`/admin/payouts${qs}`);
      setRows(r.data || []);
    } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [stateFilter]);
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <label className="text-sm">
          State:
          <select className="avcp-input ml-2" value={stateFilter}
                  onChange={(e) => setStateFilter(e.target.value)}>
            <option value="">All</option>
            {["calculated", "queued", "released", "acknowledged", "held_for_review"].map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </label>
      </div>
      <table className="w-full text-sm" data-testid="admin-payouts-table">
        <thead className="text-xs text-[var(--avcp-ink-soft)]">
          <tr><th className="text-left">Created</th><th>Invoice</th><th>State</th>
              <th>Hold reason</th><th className="text-right">Amount</th></tr>
        </thead>
        <tbody>
          {loading && <tr><td colSpan={5} className="py-3">Loading…</td></tr>}
          {!loading && rows.length === 0 && (
            <tr><td colSpan={5} className="py-3 text-[var(--avcp-ink-soft)]">No payouts.</td></tr>
          )}
          {rows.map((r) => (
            <tr key={r.id} data-testid="admin-payouts-row"
                className="border-t border-[var(--avcp-line)]">
              <td className="py-2">{fmt(r.created_at)}</td>
              <td className="font-mono text-xs">#{r.invoice_id}</td>
              <td><span className="avcp-pill">{r.state}</span></td>
              <td className="text-xs">{r.hold_reason || "—"}</td>
              <td className="text-right">{money(r.amount, r.currency)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
